import { useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '@/lib/api'
import { ChevronRight, ChevronDown, ShieldCheck, AlertTriangle, XCircle, MinusCircle, CheckCircle2 } from 'lucide-react'

type Status = 'ok' | 'expected' | 'skipped' | 'warn' | 'error' | 'critical'

interface Check { dimension: string; status: Status; message: string; metric: Record<string, any> }
interface Dataset {
  dataset_key: string; label: string; tier: string; status: Status
  owner_job: string | null; provider: string | null; notes: string | null; frozen_by_design: boolean
  checks: Check[]
}
interface Tier { tier: string; status: Status; datasets: Dataset[] }
interface Report {
  run_id: string | null; generated_at: string | null; message?: string
  summary: { datasets: number; healthy_datasets: number; checks: number; health_score: number
    ok: number; warn: number; critical: number; error: number; expected: number; skipped: number }
  tiers: Tier[]
}

const TIER_LABELS: Record<string, string> = {
  portfolio: 'Portfolio (critical)', active_universe: 'Active universe',
  market: 'Market data', benchmark_factor: 'Benchmarks & factors',
  reference: 'Reference / master', legacy_frozen: 'Legacy (frozen)',
}
const DIM_LABELS: Record<string, string> = {
  freshness: 'Freshness', validity: 'Validity / nulls', uniqueness: 'Uniqueness / dedup',
  completeness: 'Completeness', continuity: 'Continuity / gaps', unit: 'Unit consistency',
  semantic: 'Reference integrity',
}

function statusStyle(s: Status) {
  switch (s) {
    case 'ok': return { dot: 'bg-green-500', text: 'text-green-600', chip: 'bg-green-500/10 text-green-700', label: 'OK' }
    case 'expected': return { dot: 'bg-slate-400', text: 'text-slate-500', chip: 'bg-slate-500/10 text-slate-600', label: 'Expected' }
    case 'skipped': return { dot: 'bg-slate-300', text: 'text-slate-400', chip: 'bg-slate-400/10 text-slate-500', label: 'Skipped' }
    case 'warn': return { dot: 'bg-amber-500', text: 'text-amber-600', chip: 'bg-amber-500/10 text-amber-700', label: 'Warn' }
    case 'error': return { dot: 'bg-red-500', text: 'text-red-600', chip: 'bg-red-500/10 text-red-700', label: 'Error' }
    case 'critical': return { dot: 'bg-red-600', text: 'text-red-700', chip: 'bg-red-600/10 text-red-700', label: 'Critical' }
  }
}

function StatusPill({ s }: { s: Status }) {
  const st = statusStyle(s)
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${st.chip}`}>
    <span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} />{st.label}
  </span>
}

export default function DataValidation() {
  const [open, setOpen] = useState<Record<string, boolean>>({})
  const { data, isLoading } = useQuery<Report>({
    queryKey: ['data-quality'],
    queryFn: () => api.get('/data/quality').then((r) => r.data),
  })

  if (isLoading) return <div className="flex justify-center p-8"><span className="spinner" /></div>

  if (!data || !data.run_id) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold">Data Quality</h1>
        <div className="bg-card border border-border rounded-lg p-8 text-center text-muted-foreground">
          {data?.message || 'No data-quality snapshot yet — run the data-quality job.'}
        </div>
      </div>
    )
  }

  const s = data.summary
  const scoreColor = s.health_score >= 90 ? 'text-green-600' : s.health_score >= 75 ? 'text-amber-600' : 'text-red-600'

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Data Quality</h1>
          <p className="text-muted-foreground">
            Control center across all Plutus datasets — freshness, validity, uniqueness, completeness, continuity, units, reference integrity.
          </p>
        </div>
        <div className="text-right">
          <div className={`text-3xl font-bold ${scoreColor}`}>{s.health_score}<span className="text-lg text-muted-foreground">/100</span></div>
          <div className="text-xs text-muted-foreground">
            snapshot {data.generated_at ? new Date(data.generated_at).toLocaleString() : '—'}
          </div>
        </div>
      </div>

      {/* Summary chips */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <SummaryCard icon={<ShieldCheck className="h-4 w-4" />} label="Healthy datasets" value={`${s.healthy_datasets}/${s.datasets}`} tone="text-foreground" />
        <SummaryCard icon={<CheckCircle2 className="h-4 w-4" />} label="OK" value={s.ok} tone="text-green-600" />
        <SummaryCard icon={<AlertTriangle className="h-4 w-4" />} label="Warnings" value={s.warn} tone={s.warn ? 'text-amber-600' : 'text-muted-foreground'} />
        <SummaryCard icon={<XCircle className="h-4 w-4" />} label="Critical" value={s.critical + s.error} tone={(s.critical + s.error) ? 'text-red-600' : 'text-muted-foreground'} />
        <SummaryCard icon={<MinusCircle className="h-4 w-4" />} label="Expected (frozen)" value={s.expected} tone="text-slate-500" />
        <SummaryCard icon={<ShieldCheck className="h-4 w-4" />} label="Checks run" value={s.checks} tone="text-foreground" />
      </div>

      {/* Tiers */}
      {data.tiers.map((tier) => (
        <div key={tier.tier} className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <h2 className="font-semibold">{TIER_LABELS[tier.tier] || tier.tier}</h2>
            <StatusPill s={tier.status} />
          </div>
          <div className="divide-y divide-border">
            {tier.datasets.map((ds) => {
              const isOpen = open[ds.dataset_key]
              return (
                <div key={ds.dataset_key}>
                  <button
                    onClick={() => setOpen((o) => ({ ...o, [ds.dataset_key]: !o[ds.dataset_key] }))}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/40 text-left"
                  >
                    {isOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{ds.label}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {ds.provider || '—'}{ds.owner_job ? ` · job: ${ds.owner_job}` : ''}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {ds.checks.map((c) => (
                        <span key={c.dimension} title={`${DIM_LABELS[c.dimension] || c.dimension}: ${c.status}`}
                          className={`h-2 w-2 rounded-full ${statusStyle(c.status).dot}`} />
                      ))}
                    </div>
                    <div className="w-20 text-right shrink-0"><StatusPill s={ds.status} /></div>
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-3 pl-11 space-y-2">
                      {ds.notes && <div className="text-xs text-muted-foreground italic">{ds.notes}</div>}
                      <table className="w-full text-sm">
                        <tbody>
                          {ds.checks.map((c) => {
                            const st = statusStyle(c.status)
                            return (
                              <tr key={c.dimension} className="border-t border-border/50">
                                <td className="py-1.5 pr-3 w-40 align-top">
                                  <span className={`font-medium ${st.text}`}>{DIM_LABELS[c.dimension] || c.dimension}</span>
                                </td>
                                <td className="py-1.5 pr-3 w-20 align-top"><StatusPill s={c.status} /></td>
                                <td className="py-1.5 text-muted-foreground align-top">{c.message}</td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

function SummaryCard({ icon, label, value, tone }: { icon: ReactNode; label: string; value: ReactNode; tone: string }) {
  return (
    <div className="bg-card border border-border rounded-lg p-3">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">{icon}{label}</div>
      <div className={`text-xl font-bold mt-1 ${tone}`}>{value}</div>
    </div>
  )
}
